/**
 * NetworkStyleContext - Contexto global para estilos de Red Urbana
 *
 * @module core/spatial/contexts
 * @version 1.0
 * @created 2026-03-02
 *
 * @description
 * Proporciona estado global y funciones para gestionar los estilos visuales
 * de la Red Vial Canónica. Permite al usuario personalizar la apariencia de
 * la red sin necesidad de modificar código.
 *
 * Funcionalidad:
 * - Mantener estilos actuales en estado React
 * - Persistir estilos en localStorage (por proyecto)
 * - Aplicar presets predefinidos
 * - Resetear a valores por defecto
 *
 * @example
 * ```typescript
 * // En un componente
 * const { style, updateStyle, applyPreset } = useNetworkStyle();
 *
 * // Cambiar color de edges
 * updateStyle({
 *   edges: { ...style.edges, color: '#FF0000' }
 * });
 *
 * // Aplicar preset
 * applyPreset('highContrast');
 * ```
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  NetworkStyleConfig,
  DEFAULT_NETWORK_STYLE,
  NETWORK_STYLE_PRESETS,
  isValidNetworkStyle,
} from '../config/network-style.config';
import { useProject } from '../../projects';

/**
 * Valor del contexto de estilos de red
 */
interface NetworkStyleContextValue {
  /** Configuración de estilos actual */
  style: NetworkStyleConfig;
  /** Actualiza parcialmente la configuración de estilos */
  updateStyle: (updates: Partial<NetworkStyleConfig>) => void;
  /** Aplica un preset predefinido */
  applyPreset: (presetName: keyof typeof NETWORK_STYLE_PRESETS) => void;
  /** Resetea a valores por defecto */
  resetToDefault: () => void;
  /** Indica si hay cambios sin guardar */
  hasUnsavedChanges: boolean;
}

/**
 * Contexto de estilos de red (interno)
 */
const NetworkStyleContext = createContext<NetworkStyleContextValue | null>(null);

/**
 * Props del Provider
 */
interface NetworkStyleProviderProps {
  children: ReactNode;
}

/**
 * Genera la clave de localStorage para un proyecto
 *
 * @param projectId - ID del proyecto
 * @returns Clave de localStorage
 */
function getStorageKey(projectId: string): string {
  return `network-style:${projectId}`;
}

/**
 * Carga estilos desde localStorage para un proyecto
 *
 * @param projectId - ID del proyecto
 * @returns Configuración guardada o null si no existe
 */
function loadStyleFromStorage(projectId: string): NetworkStyleConfig | null {
  try {
    const stored = localStorage.getItem(getStorageKey(projectId));
    if (!stored) return null;

    const parsed = JSON.parse(stored) as NetworkStyleConfig;

    // Validar que la configuración cargada sea válida
    if (!isValidNetworkStyle(parsed)) {
      console.warn('[NetworkStyleContext] Invalid style in localStorage, using default');
      return null;
    }

    return parsed;
  } catch (error) {
    console.error('[NetworkStyleContext] Error loading style from storage:', error);
    return null;
  }
}

/**
 * Guarda estilos en localStorage para un proyecto
 *
 * @param projectId - ID del proyecto
 * @param style - Configuración a guardar
 */
function saveStyleToStorage(projectId: string, style: NetworkStyleConfig): void {
  try {
    localStorage.setItem(getStorageKey(projectId), JSON.stringify(style));
  } catch (error) {
    console.error('[NetworkStyleContext] Error saving style to storage:', error);
  }
}

/**
 * Provider del contexto de estilos de red
 *
 * @param props - Props del componente
 * @returns Provider component
 */
export function NetworkStyleProvider({ children }: NetworkStyleProviderProps): JSX.Element {
  const { projectId } = useProject();
  const [style, setStyle] = useState<NetworkStyleConfig>(DEFAULT_NETWORK_STYLE);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Siempre iniciar con red urbana blanca (DEFAULT) al abrir un proyecto.
  // Los estilos personalizados (sintaxis espacial, etc.) deben aplicarse explícitamente.
  useEffect(() => {
    setStyle(DEFAULT_NETWORK_STYLE);
    setHasUnsavedChanges(false);
  }, [projectId]);

  /**
   * Actualiza parcialmente la configuración de estilos
   */
  const updateStyle = useCallback(
    (updates: Partial<NetworkStyleConfig>) => {
      const newStyle = {
        ...style,
        ...updates,
        edges: updates.edges ? { ...style.edges, ...updates.edges } : style.edges,
        nodes: updates.nodes ? { ...style.nodes, ...updates.nodes } : style.nodes,
      };

      // Validar antes de aplicar
      if (!isValidNetworkStyle(newStyle)) {
        console.error('[NetworkStyleContext] Invalid style update, ignoring');
        return;
      }

      setStyle(newStyle);
      setHasUnsavedChanges(true);

      // Auto-guardar después de 500ms de inactividad
      if (projectId) {
        setTimeout(() => {
          saveStyleToStorage(projectId, newStyle);
          setHasUnsavedChanges(false);
        }, 500);
      }
    },
    [style, projectId]
  );

  /**
   * Aplica un preset predefinido
   */
  const applyPreset = useCallback(
    (presetName: keyof typeof NETWORK_STYLE_PRESETS) => {
      const preset = NETWORK_STYLE_PRESETS[presetName];
      if (!preset) {
        console.error(`[NetworkStyleContext] Unknown preset: ${presetName}`);
        return;
      }

      setStyle(preset);
      setHasUnsavedChanges(true);

      // Guardar inmediatamente
      if (projectId) {
        saveStyleToStorage(projectId, preset);
        setHasUnsavedChanges(false);
      }
    },
    [projectId]
  );

  /**
   * Resetea a valores por defecto
   */
  const resetToDefault = useCallback(() => {
    setStyle(DEFAULT_NETWORK_STYLE);
    setHasUnsavedChanges(true);

    // Guardar inmediatamente
    if (projectId) {
      saveStyleToStorage(projectId, DEFAULT_NETWORK_STYLE);
      setHasUnsavedChanges(false);
    }
  }, [projectId]);

  const value: NetworkStyleContextValue = {
    style,
    updateStyle,
    applyPreset,
    resetToDefault,
    hasUnsavedChanges,
  };

  return (
    <NetworkStyleContext.Provider value={value}>
      {children}
    </NetworkStyleContext.Provider>
  );
}

/**
 * Hook para acceder al contexto de estilos de red
 *
 * @returns Valor del contexto
 * @throws Error si se usa fuera del Provider
 *
 * @example
 * ```typescript
 * const { style, updateStyle } = useNetworkStyle();
 *
 * // Cambiar ancho de líneas
 * updateStyle({
 *   edges: { ...style.edges, width: 4 }
 * });
 * ```
 */
export function useNetworkStyle(): NetworkStyleContextValue {
  const context = useContext(NetworkStyleContext);
  if (!context) {
    throw new Error('useNetworkStyle must be used within NetworkStyleProvider');
  }
  return context;
}
